import type { ReactElement, SVGProps } from 'react';
import {
  faArrowDown,
  faArrowLeft,
  faArrowPointer,
  faArrowRight,
  faArrowUp,
  faArrowUpFromBracket,
  faArrowUpRightFromSquare,
  faBan,
  faBell,
  faPause,
  faBoxArchive,
  faBars,
  faBolt,
  faBookOpen,
  faBug,
  faBuilding,
  faCalendar,
  faChampagneGlasses,
  faChartBar,
  faCheck,
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faCircle,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCircleMinus,
  faCircleNotch,
  faCirclePlay,
  faCircleXmark,
  faClapperboard,
  faClock,
  faClone,
  faCode,
  faCodeBranch,
  faCodeMerge,
  faComment,
  faCommentDots,
  faCompass,
  faCompress,
  faCopy,
  faDatabase,
  faDiagramProject,
  faDisplay,
  faDownload,
  faEnvelope,
  faEnvelopeCircleCheck,
  faEnvelopeOpenText,
  faExpand,
  faEye,
  faEyeSlash,
  faFileLines,
  faFilm,
  faFlag,
  faFlask,
  faFloppyDisk,
  faFolderOpen,
  faForwardStep,
  faGaugeHigh,
  faGlobe,
  faGripVertical,
  faHourglassHalf,
  faICursor,
  faImage,
  faKey,
  faLayerGroup,
  faLightbulb,
  faLink,
  faListCheck,
  faListOl,
  faLock,
  faLockOpen,
  faMagnifyingGlass,
  faMessage,
  faMobileScreenButton,
  faMoon,
  faPaperPlane,
  faPaperclip,
  faPenToSquare,
  faPlay,
  faPlug,
  faPlugCircleBolt,
  faPlugCircleXmark,
  faPlus,
  faPrint,
  faPuzzlePiece,
  faRightFromBracket,
  faRobot,
  faRotate,
  faRotateLeft,
  faScroll,
  faShareNodes,
  faShieldHalved,
  faShieldVirus,
  faSort,
  faSpinner,
  faStar,
  faStopwatch,
  faSun,
  faTable,
  faTableCells,
  faTableList,
  faTabletScreenButton,
  faToolbox,
  faTowerBroadcast,
  faTrash,
  faTriangleExclamation,
  faUser,
  faUserGear,
  faUserMinus,
  faUserPlus,
  faUsers,
  faWandMagic,
  faWandMagicSparkles,
  faWaveSquare,
  faXmark,
  type IconDefinition,
  faDeleteLeft,
  faHouse,
  faKeyboard,
} from '@fortawesome/free-solid-svg-icons';

/**
 * App icons: Font Awesome 6 Free Solid, as in the Figma UI kit (Spacing, Avatars & Icons > Iconography, node 1:3496).
 * Exported under the names the app already uses, so swapping icon sets only changes imports. Icons fill
 * with currentColor and default to 24px like before; size them with className (size-4, …).
 * Font Awesome Free icons: CC BY 4.0, https://fontawesome.com/license/free
 */
export type IconProps = SVGProps<SVGSVGElement>;
export type IconComponent = (props: IconProps) => ReactElement;

function fa(def: IconDefinition, name: string): IconComponent {
  const [width, height, , , path] = def.icon;
  const Icon = (props: IconProps) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox={`0 0 ${width} ${height}`} fill="currentColor" aria-hidden="true" focusable="false" {...props}>
      {Array.isArray(path) ? path.map((d) => <path key={d} d={d} />) : <path d={path} />}
    </svg>
  );
  Icon.displayName = name;
  return Icon;
}

// Icons the Figma kit shows by name.
export const Activity = fa(faWaveSquare, 'Activity');
export const AlertTriangle = fa(faTriangleExclamation, 'AlertTriangle');
export const BarChart3 = fa(faChartBar, 'BarChart3');
export const Braces = fa(faCode, 'Braces');
export const Bug = fa(faBug, 'Bug');
export const CalendarClock = fa(faCalendar, 'CalendarClock');
export const CheckCircle2 = fa(faCircleCheck, 'CheckCircle2');
export const CirclePlay = fa(faCirclePlay, 'CirclePlay');
export const Clock3 = fa(faClock, 'Clock3');
export const Copy = fa(faClone, 'Copy');
export const Download = fa(faDownload, 'Download');
export const Upload = fa(faArrowUpFromBracket, 'Upload');
export const Package = fa(faBoxArchive, 'Package');
export const FileText = fa(faFileLines, 'FileText');
export const GitBranch = fa(faCodeBranch, 'GitBranch');
export const Layers = fa(faLayerGroup, 'Layers');
export const LayoutDashboard = fa(faTableCells, 'LayoutDashboard');
export const Link2 = fa(faLink, 'Link2');
export const Loader2 = fa(faSpinner, 'Loader2');
export const MinusCircle = fa(faCircleMinus, 'MinusCircle');
export const Pencil = fa(faPenToSquare, 'Pencil');
export const Plus = fa(faPlus, 'Plus');
export const RefreshCw = fa(faRotate, 'RefreshCw');
export const Search = fa(faMagnifyingGlass, 'Search');
export const Share2 = fa(faShareNodes, 'Share2');
export const Trash2 = fa(faTrash, 'Trash2');
export const TriangleAlert = fa(faTriangleExclamation, 'TriangleAlert');
export const Webhook = fa(faLink, 'Webhook');
export const XCircle = fa(faCircleXmark, 'XCircle');
export const Zap = fa(faBolt, 'Zap');

// Closest Font Awesome Solid match for the rest.
export const ArrowDown = fa(faArrowDown, 'ArrowDown');
export const ArrowLeft = fa(faArrowLeft, 'ArrowLeft');
export const ArrowRight = fa(faArrowRight, 'ArrowRight');
export const ArrowUp = fa(faArrowUp, 'ArrowUp');
export const ArrowUpRight = fa(faArrowUpRightFromSquare, 'ArrowUpRight');
export const Ban = fa(faBan, 'Ban');
export const Blocks = fa(faPuzzlePiece, 'Blocks');
export const BookOpenText = fa(faBookOpen, 'BookOpenText');
export const Bot = fa(faRobot, 'Bot');
export const Building2 = fa(faBuilding, 'Building2');
export const Check = fa(faCheck, 'Check');
export const ChevronDown = fa(faChevronDown, 'ChevronDown');
export const ChevronRight = fa(faChevronRight, 'ChevronRight');
export const ChevronUp = fa(faChevronUp, 'ChevronUp');
export const ChevronsUpDown = fa(faSort, 'ChevronsUpDown');
export const Circle = fa(faCircle, 'Circle');
export const CircleAlert = fa(faCircleExclamation, 'CircleAlert');
export const CircleDashed = fa(faCircleNotch, 'CircleDashed');
export const Clapperboard = fa(faClapperboard, 'Clapperboard');
export const Compass = fa(faCompass, 'Compass');
export const CopyPlus = fa(faCopy, 'CopyPlus');
export const Database = fa(faDatabase, 'Database');
export const ExternalLink = fa(faArrowUpRightFromSquare, 'ExternalLink');
export const Eye = fa(faEye, 'Eye');
export const EyeOff = fa(faEyeSlash, 'EyeOff');
export const Film = fa(faFilm, 'Film');
export const Flag = fa(faFlag, 'Flag');
export const FlaskConical = fa(faFlask, 'FlaskConical');
export const FolderKanban = fa(faFolderOpen, 'FolderKanban');
export const Gauge = fa(faGaugeHigh, 'Gauge');
export const Globe = fa(faGlobe, 'Globe');
export const GripVertical = fa(faGripVertical, 'GripVertical');
export const Hourglass = fa(faHourglassHalf, 'Hourglass');
export const Image = fa(faImage, 'Image');
export const ImageOff = fa(faEyeSlash, 'ImageOff');
export const Info = fa(faCircleInfo, 'Info');
export const KeyRound = fa(faKey, 'KeyRound');
export const Lightbulb = fa(faLightbulb, 'Lightbulb');
export const ListChecks = fa(faListCheck, 'ListChecks');
export const ListOrdered = fa(faListOl, 'ListOrdered');
export const Lock = fa(faLock, 'Lock');
export const LogOut = fa(faRightFromBracket, 'LogOut');
export const Mail = fa(faEnvelope, 'Mail');
export const MailCheck = fa(faEnvelopeCircleCheck, 'MailCheck');
export const MailPlus = fa(faEnvelopeOpenText, 'MailPlus');
export const Maximize2 = fa(faExpand, 'Maximize2');
export const Menu = fa(faBars, 'Menu');
export const Merge = fa(faCodeMerge, 'Merge');
export const MessageCircle = fa(faComment, 'MessageCircle');
export const MessageSquare = fa(faMessage, 'MessageSquare');
export const MessageSquareText = fa(faCommentDots, 'MessageSquareText');
export const Minimize2 = fa(faCompress, 'Minimize2');
export const MonitorSmartphone = fa(faDisplay, 'MonitorSmartphone');
export const Moon = fa(faMoon, 'Moon');
export const MousePointerClick = fa(faArrowPointer, 'MousePointerClick');
export const Paperclip = fa(faPaperclip, 'Paperclip');
export const PartyPopper = fa(faChampagneGlasses, 'PartyPopper');
export const Play = fa(faPlay, 'Play');
export const Pause = fa(faPause, 'Pause');
export const Plug = fa(faPlug, 'Plug');
export const PlugZap = fa(faPlugCircleBolt, 'PlugZap');
export const Printer = fa(faPrint, 'Printer');
export const Radio = fa(faTowerBroadcast, 'Radio');
export const RotateCcw = fa(faRotateLeft, 'RotateCcw');
export const Rows3 = fa(faTableList, 'Rows3');
export const Save = fa(faFloppyDisk, 'Save');
export const ScrollText = fa(faScroll, 'ScrollText');
export const Send = fa(faPaperPlane, 'Send');
export const Bell = fa(faBell, 'Bell');
export const ShieldAlert = fa(faShieldVirus, 'ShieldAlert');
export const ShieldCheck = fa(faShieldHalved, 'ShieldCheck');
export const ShieldOff = fa(faLockOpen, 'ShieldOff');
export const SkipForward = fa(faForwardStep, 'SkipForward');
export const Toolbox = fa(faToolbox, 'Toolbox');
export const Smartphone = fa(faMobileScreenButton, 'Smartphone');
export const Sparkles = fa(faWandMagicSparkles, 'Sparkles');
export const Star = fa(faStar, 'Star');
export const Sun = fa(faSun, 'Sun');
export const Table2 = fa(faTable, 'Table2');
export const TabletSmartphone = fa(faTabletScreenButton, 'TabletSmartphone');
export const TextSelect = fa(faICursor, 'TextSelect');
export const Timer = fa(faStopwatch, 'Timer');
export const User = fa(faUser, 'User');
export const UserCog = fa(faUserGear, 'UserCog');
export const UserMinus = fa(faUserMinus, 'UserMinus');
export const UserPlus = fa(faUserPlus, 'UserPlus');
export const Users = fa(faUsers, 'Users');
export const Wand2 = fa(faWandMagic, 'Wand2');
export const WifiOff = fa(faPlugCircleXmark, 'WifiOff');
export const Workflow = fa(faDiagramProject, 'Workflow');
export const X = fa(faXmark, 'X');
export const DeleteLeft = fa(faDeleteLeft, 'DeleteLeft');
export const House = fa(faHouse, 'House');
export const Keyboard = fa(faKeyboard, 'Keyboard');
